import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';
import PasswordVerifyService from '../services/PasswordVerifyService';
import {
  PasswordValidationResult,
  UsePasswordValidationProps,
  UsePasswordValidationReturn,
  VALIDATION_MESSAGES,
} from '../types';

const usePasswordValidation = ({
  setValue,
  passwordFieldName,
  confirmPasswordFieldName,
  validationSchema,
  enforceValidation = false,
}: UsePasswordValidationProps): UsePasswordValidationReturn => {
  const passwordVerifyService = useMemo(() => new PasswordVerifyService(), []);

  const validatePasswordRules = useMemo(() => {
    if (validationSchema && enforceValidation) {
      return (password: string): PasswordValidationResult => {
        try {
          validationSchema.parse(password);

          return {
            lengthValid: true,
            maxLength: true,
            capitalLetter: true,
            lowercaseLetter: true,
            specialCharacters: true,
            containNumbers: true,
            noWhitespace: true,
            isValid: true,
          };
        } catch (error) {
          if (error instanceof z.ZodError) {
            const issues = error.issues.map((issue) => issue.message);

            return {
              lengthValid: !issues.includes(VALIDATION_MESSAGES.lengthValid),
              maxLength: !issues.includes(VALIDATION_MESSAGES.maxLength),
              capitalLetter: !issues.includes(
                VALIDATION_MESSAGES.capitalLetter
              ),
              lowercaseLetter: !issues.includes(
                VALIDATION_MESSAGES.lowercaseLetter
              ),
              specialCharacters: !issues.includes(
                VALIDATION_MESSAGES.specialCharacters
              ),
              containNumbers: !issues.includes(
                VALIDATION_MESSAGES.containNumbers
              ),
              noWhitespace: !issues.includes(VALIDATION_MESSAGES.noWhitespace),
              isValid: false,
            };
          }
          return passwordVerifyService.validatePassword(password);
        }
      };
    }

    return passwordVerifyService.validatePassword.bind(passwordVerifyService);
  }, [validationSchema, enforceValidation, passwordVerifyService]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showValidationMessages, setShowValidationMessages] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [currentConfirmPassword, setCurrentConfirmPassword] = useState('');
  const [isPasswordValid, setIsPasswordValid] =
    useState<PasswordValidationResult>(() =>
      validationSchema && enforceValidation
        ? {
            lengthValid: false,
            maxLength: true,
            capitalLetter: false,
            lowercaseLetter: false,
            specialCharacters: false,
            containNumbers: false,
            noWhitespace: true,
            isValid: false,
          }
        : passwordVerifyService.validatePassword('')
    );

  const toggleShowPassword = () => setShowPassword((prev) => !prev);
  const toggleShowConfirmPassword = () =>
    setShowConfirmPassword((prev) => !prev);
  const showValidations = () => setShowValidationMessages(true);
  const hideValidations = () => setShowValidationMessages(false);

  const validatePassword = useCallback(
    (value: string) => {
      const validationResult = validatePasswordRules(value);
      setIsPasswordValid(validationResult);
      setCurrentPassword(value);
      setValue(passwordFieldName, value);

      if (confirmPasswordFieldName && currentConfirmPassword) {
        const isMatch = currentConfirmPassword === value;
        setIsConfirmed(!isMatch);
      }
    },
    [
      passwordFieldName,
      setValue,
      validatePasswordRules,
      confirmPasswordFieldName,
      currentConfirmPassword,
    ]
  );

  const validateConfirmPassword = useCallback(
    (value: string) => {
      if (!confirmPasswordFieldName) return;

      setCurrentConfirmPassword(value);
      const isMatch = value === currentPassword;
      setIsConfirmed(!isMatch);
      setValue(confirmPasswordFieldName, value);
    },
    [confirmPasswordFieldName, currentPassword, setValue]
  );

  return {
    showPassword,
    showConfirmPassword,
    isPasswordValid,
    isConfirmed,
    showValidationMessages,
    toggleShowPassword,
    toggleShowConfirmPassword,
    validatePassword,
    validateConfirmPassword,
    showValidations,
    hideValidations,
  };
};

export default usePasswordValidation;
